import { setItem } from 'utils/storageUtils'
import { matchPath } from 'react-router-dom'
import { LANGUAGE_CODES } from 'constants/languageList'

/**
 * @constant {Object} Routes
 * @description Object containing all application route patterns.
 * Each route includes a languageCode parameter and specific route parameters.
 * These routes are used for navigation and URL pattern matching throughout the application.
 */
export const Routes = {
  homepage: '/:languageCode',
  casinoSubCategory: '/:languageCode/:casinoCategory/:casinoSubCategory',
  promotionpage: '/:languageCode/promotion',
  promotionpageId: '/:languageCode/promotion/:bonusId',
  userpromotionpageId: '/:languageCode/promotion/:bonusId/:userBonusId',
  loyaltyBonusPage: '/:languageCode/loyalty-bonus',
  sportsbook: '/:languageCode/sports',
  playGame: '/:languageCode/play/:gameId/:provider/:gameIdentifier',
  mobilePlayGame: '/:languageCode/play-mobile/:gameId/:provider/:gameIdentifier',
  demoGame: '/:languageCode/demo/:gameId/:provider/:gameIdentifier',
  login: '/:languageCode/login',
  signup: '/:languageCode/signup',
  account: '/:languageCode/account/:section',
  forgotPassword: '/:languageCode/forgot-password',
  notFound: '/:languageCode/404',
  forbidden: '/:languageCode/403',
  siteMaintenance: '/:languageCode/site-maintenance',
  siteMaintenanceStatic: '/:languageCode/maintenance',
  blockedCountry: '/:languageCode/600',
  emailTokenLink: '/:languageCode/verify-email',
  resetPassword: '/:languageCode/reset-password',
  resetEmail: '/:languageCode/reset-email',
  resendEmail: '/:languageCode/resend-email',
  // tenantInactive: '/:languageCode/tenant-inactive',
  cms: '/:languageCode/:slug',
  paymentStatus: '/:languageCode/payment-status/:type',
  CashbackBonus: '/:languageCode/cashback-bonus',
  paymentSuccess: '/:languageCode/account/:section/status',
  // tournaments: '/:languageCode/tournaments',
  // tournamentDetails: '/:languageCode/tournaments/:tournamentId',
  VIP: '/:languageCode/vip',
  affiliate: '/:languageCode/affiliate',
  home: ':languageCode/home',
  calendar: '/:languageCode/calendar',
  wpAdmin: '/:languageCode/wp-admin'
}

/**
 * @constant {Array<string>} validRoutesWithoutLanguageCode
 * @description Array of valid routes with the languageCode parameter removed.
 * Created by mapping through Routes object values and removing the '/:languageCode' prefix.
 * Used for route validation and matching without language code consideration.
 */
export const validRoutesWithoutLanguageCode = Object.values(Routes)
  .map(route => route.replace('/:languageCode', ''))
  .filter(Boolean)

/**
 * Removes one or more trailing slashes from a path.
 * The canonical URL form across the app is slash-less (e.g. `/en`, `/en/promotion`),
 * so this is used both for route matching and when building redirect targets.
 *
 * @param {string} path - The path to normalize
 * @returns {string} - The path without any trailing slash
 */
export const stripTrailingSlash = (path) => path?.replace(/\/+$/, '') || ''

/**
 * @constant {Object} SEO_META_CONFIG
 * @description Maps specific routes or sections to their corresponding SEO meta tags.
 *              We are using the value of the keys to match the keys in the languageData.
 *              The keys in the languageData are prefixed with 'seo_'.
 *              This configuration is used to enhance the SEO of the application.
 */

/**
 * Checks if a path is a valid CMS route by validating the slug against the CMS list
 *
 * @param {string} path - The path to check (e.g., "/privacy-policy")
 * @param {Array} cmsList - The list of CMS pages from the API
 * @returns {boolean} - True if the path is a valid CMS route, false otherwise
 */
export const isValidCmsRoute = (path, cmsList) => {
  const normalizedPath = stripTrailingSlash(path?.split(/[?#]/)[0]) || '/'

  if (normalizedPath.startsWith('/') && normalizedPath.split('/').length === 2) {
    const potentialSlug = normalizedPath.substring(1)
    return cmsList?.rows?.some(cms => cms.slug === potentialSlug) || false
  }
  return false
}

/**
 * Checks if a path is a valid lobby route by validating the casino category
 * against the categories returned by the home-data API.
 *
 * @param {string} path - The path to check (e.g., "/casino/all")
 * @param {Object|Array} casinoCategories - The casino categories from the API
 * @returns {boolean} - True if the category exists, false otherwise
 */
export const isLobbyRoute = (path, casinoCategories) => {
  const cleanPath = stripTrailingSlash(path?.split(/[?#]/)[0]) || '/'
  const match = matchPath({ path: '/:casinoCategory/:casinoSubCategory', end: true }, cleanPath)

  if (!match?.params?.casinoCategory) {
    return false
  }

  const categoryRows = casinoCategories?.rows || casinoCategories || []
  return categoryRows.some(category => category?.slug === match.params.casinoCategory)
}

/**
 * Checks if a path is the "all" lobby of a valid casino category (e.g. "/casino/all"),
 * i.e. a lobby route whose sub-category segment is the literal `all`.
 *
 * @param {string} path - The path to check, without language code (e.g., "/casino/all")
 * @param {Object|Array} casinoCategories - The casino categories from the API
 * @returns {boolean} - True if the path is a lobby "all" route, false otherwise
 */
export const isLobbyAllRoute = (path, casinoCategories) => {
  const cleanPath = stripTrailingSlash(path?.split(/[?#]/)[0]).trim() || '/'
  return cleanPath.endsWith('/all') && isLobbyRoute(cleanPath, casinoCategories)
}

/**
 * Checks if a path is a valid static route by comparing with validRoutesWithoutLanguageCode
 *
 * @param {string} path - The path to check (e.g., "/verify-email")
 * @returns {boolean} - True if the path is a valid static route, false otherwise
 */
export const isValidStaticRoute = (path) => {
  if (typeof path !== 'string') return false

  const cleanPath = stripTrailingSlash(path.split(/[?#]/)[0]) || '/'

  return validRoutesWithoutLanguageCode.some((pattern) => {
    if (pattern === '/:slug' || pattern === '/:casinoCategory/:casinoSubCategory') {
      return false
    }

    if (pattern.includes('/:')) {
      return !!matchPath({ path: pattern, end: true }, cleanPath)
    }

    return pattern === cleanPath
  })
}

/**
 * Validates if a route is valid by checking against both static routes and CMS routes
 *
 * @param {string} path - The path to check
 * @param {Array} cmsList - The list of CMS pages from the API
 * @param {Object|Array} casinoCategories - The casino categories from the API
 * @returns {boolean} - True if the route is valid, false otherwise
 */
export const isValidRoute = (path, cmsList, casinoCategories) => {
  return isValidStaticRoute(path) || isValidCmsRoute(path, cmsList) || isLobbyRoute(path, casinoCategories)
}

/**
 * Decides whether a route change should reset the window scroll to the top.
 * Skipped when only the language prefix changed (same page), and when moving between
 * casino lobby routes, where the lobby manages its own scroll (see AMGP-573).
 *
 * @param {string} previousPathname - Pathname before navigation, with language code (e.g., "/en/promotion")
 * @param {string} nextPathname - Pathname after navigation, with language code (e.g., "/en/promotion/2076")
 * @param {Object|Array} casinoCategories - The casino categories from the API
 * @returns {boolean} - True if the page should be scrolled to the top
 */
export const shouldScrollToTopOnRouteChange = (previousPathname, nextPathname, casinoCategories) => {
  const withoutLanguage = (pathname) =>
    stripTrailingSlash(`/${(pathname || '').split('/').filter(Boolean).slice(1).join('/')}`) || '/'

  const previousPath = withoutLanguage(previousPathname)
  const nextPath = withoutLanguage(nextPathname)

  if (previousPath === nextPath) return false

  return !(isLobbyRoute(previousPath, casinoCategories) && isLobbyRoute(nextPath, casinoCategories))
}

/**
 * Extracts language code from URL path
 *
 * @param {string} path - The URL path
 * @returns {string|null} - The language code or null if not found
 */
export const extractLanguageCodeFromPath = (path) => {
  const segments = path.split('/').filter(segment => segment)
  return segments.length > 0 ? segments[0].toUpperCase() : null
}

const getEnvDefaultLanguage = () => (process.env.REACT_APP_DEFAULT_LANGUAGE || 'EN').toUpperCase()

/**
 * Returns the language codes allowed on the current site (main domain or mirror).
 * The list comes from the get-languages API, optionally narrowed per root domain by the
 * `language` block of the tenant config. When it is empty (e.g. the request failed) the
 * build-time default is used so the app can still render.
 *
 * @param {Object} languages - The languages object from the API
 * @returns {Array<string>} - Uppercase language codes
 */
export const getAllowedLanguages = (languages) => {
  const allowedLanguages = languages?.allowedLanguages
  return Array.isArray(allowedLanguages) && allowedLanguages.length > 0
    ? allowedLanguages
    : [getEnvDefaultLanguage()]
}

/**
 * Resolves the default language for the current site at runtime.
 *
 * REACT_APP_DEFAULT_LANGUAGE is baked into the bundle, and the same bundle serves the main
 * domain and every mirror, so it can only be the default when the site actually allows it.
 * Otherwise the first allowed language wins, which makes the order of the per-domain lists in
 * `public/config/<tenant>.json` meaningful (first entry = that domain's default).
 * Same rule as the sitemap x-default in scripts/generate-seo-files.js.
 *
 * @param {Object} languages - The languages object from the API
 * @returns {string} - Uppercase language code
 */
export const getDefaultLanguage = (languages) => {
  const allowedLanguages = getAllowedLanguages(languages)
  const envDefault = getEnvDefaultLanguage()
  return allowedLanguages.includes(envDefault) ? envDefault : allowedLanguages[0]
}

/**
 * Validates if a language code is allowed on the current site.
 * No language is implicitly allowed: a mirror configured with only `EN-SE` must not accept
 * `EN` (AMGP-3718). English is only assumed when the allowed list itself is empty.
 *
 * @param {string} languageCode - The language code to validate (uppercase)
 * @param {Object} languages - The languages object from the API
 * @returns {boolean} - True if the language code is allowed, false otherwise
 */
export const isValidLanguageCode = (languageCode, languages) => {
  return Boolean(languageCode) && getAllowedLanguages(languages).includes(languageCode)
}

/**
 * Checks whether a URL segment is a language code the platform knows about, even when the
 * current site does not allow it. Used to drop a stale language prefix (e.g. `/en/promotion`
 * on a mirror that only allows `EN-SE`) instead of treating it as part of the route.
 *
 * @param {string} languageCode - The language code to check (uppercase)
 * @returns {boolean} - True if the code exists in LANGUAGE_CODES
 */
export const isKnownLanguageCode = (languageCode) => Boolean(languageCode && LANGUAGE_CODES[languageCode])

/**
 * Determines if a redirection is needed based on route and language validation
 * and returns the appropriate URL for redirection.
 *
 * This function encapsulates the redirection logic from the handleRouteValidation
 * function in the PreAppLoader component. It checks if the current URL has a valid
 * language code and if the route is valid, then determines if redirection is needed.
 *
 * @param {string} savedLanguage - The language saved in local storage (uppercase)
 * @param {string} urlPath - The current URL path
 * @param {Object} languages - The languages object from the API
 * @param {Array} cmsList - The list of CMS pages from the API
 * @param {Object|Array} casinoCategories - The casino categories from the API
 * @param {string} defaultLanguage - Preferred default language; only used when the site allows it,
 *                                   otherwise the site's own default applies (see getDefaultLanguage)
 * @param {string} [queryParams=''] - The query parameters to append to the URL
 * @returns {string|null} - The URL for redirection or null if no redirection is needed
 */
export const getRedirectionUrl = (
  savedLanguage,
  urlPath,
  languages,
  cmsList,
  casinoCategories,
  defaultLanguage,
  queryParams = ''
) => {
  const urlLanguageCode = extractLanguageCodeFromPath(urlPath)

  const hasLanguageCode = urlLanguageCode !== null

  const isValidLangCode = hasLanguageCode &&
    isValidLanguageCode(urlLanguageCode, languages)

  // The caller's default is a build-time value shared by every domain of the bundle, so it
  // only applies when this site allows it; otherwise fall back to the site's own default
  const resolvedDefaultLanguage = isValidLanguageCode(defaultLanguage?.toUpperCase(), languages)
    ? defaultLanguage.toUpperCase()
    : getDefaultLanguage(languages)

  if (!hasLanguageCode || !isValidLangCode) {
    let languageToUse = resolvedDefaultLanguage

    // Check if the saved language code is valid and use it, otherwise set default language
    // Example: if savedLanguage is 'EN' and it exists in allowedLanguages ['EN','DE'], use 'EN'
    // If savedLanguage is 'FR' which is not in allowedLanguages, use defaultLanguage
    if (savedLanguage && isValidLanguageCode(savedLanguage, languages)) {
      languageToUse = savedLanguage
    } else {
      setItem('language', languageToUse)
    }

    // A known but not allowed language prefix (e.g. `/en/promotion` on a mirror that only
    // allows EN-SE) is dropped so the rest of the path can still be validated and kept
    const hasStaleLanguagePrefix = hasLanguageCode && isKnownLanguageCode(urlLanguageCode)
    const pathWithoutLanguage = hasStaleLanguagePrefix
      ? stripTrailingSlash(`/${urlPath.split('/').filter(segment => segment).slice(1).join('/')}`)
      : stripTrailingSlash(urlPath)
    const isValidCurrentRoute = isValidRoute(pathWithoutLanguage, cmsList, casinoCategories)

    if (isValidCurrentRoute) {
      return `/${languageToUse.toLowerCase()}${pathWithoutLanguage}${queryParams}`
    } else {
      return `/${languageToUse.toLowerCase()}${queryParams}`
    }
  }

  // Only a saved language the site allows may override the language in the URL, otherwise a
  // stale value (e.g. 'EN' saved before a mirror dropped it) would bounce /en-se/... to /en/...
  if (savedLanguage && savedLanguage !== urlLanguageCode && isValidLanguageCode(savedLanguage, languages)) {
    const pathSegments = urlPath.split('/').filter(segment => segment)
    const pathWithoutLanguage = pathSegments.length > 1
      ? '/' + pathSegments.slice(1).join('/')
      : ''

    return `/${savedLanguage.toLowerCase()}${pathWithoutLanguage}${queryParams}`
  }

  return null
}

export const SEO_META_CONFIG = {
  homepage: 'home',
  home: 'home',
  login: 'login',
  signup: 'sign_up',
  resetPassword: 'reset_password',
  forgotPassword: 'forgot_password',
  casinoSubCategory: 'casino',
  promotionpage: 'promotion',
  loyaltyBonusPage: 'loyalty',
  sportsbook: 'sports',
  calendar: 'calendar',
  VIP: 'vip',
  affiliates: 'affiliates'
}

/**
 * @constant {Array<Object>} SITEMAP_CONFIG
 * @description Configuration for routes that should be included in the generated sitemap.xml.
 *              Each entry maps a route from the Routes object to its sitemap metadata.
 *              Only public, indexable, non-dynamic routes should be listed here.
 *
 * @property {keyof Routes} routeKey - Must be a valid key from the Routes object
 * @property {number} priority - Sitemap priority (0.0 to 1.0)
 * @property {('always'|'hourly'|'daily'|'weekly'|'monthly'|'yearly'|'never')} changefreq - How frequently the page changes
 */
export const SITEMAP_CONFIG = [
  { routeKey: 'homepage', priority: 1.0, changefreq: 'weekly' },
  { routeKey: 'promotionpage', priority: 0.8, changefreq: 'weekly' },
  { routeKey: 'loyaltyBonusPage', priority: 0.7, changefreq: 'monthly' },
  { routeKey: 'sportsbook', priority: 0.8, changefreq: 'daily' },
  { routeKey: 'VIP', priority: 0.7, changefreq: 'monthly' },
  { routeKey: 'calendar', priority: 0.6, changefreq: 'weekly' }
]

export const getRootDomain = () => {
  const hostname = window.location.hostname
  return hostname.replace(/^www\./, '')
}
